import React, { useEffect, useState, useRef } from "react";
import { Tour as AntdTour, Button, TourProps } from "antd";
import { useTour } from "../../Context/TourContext";
import { useNavigate, useLocation } from "react-router-dom";
import { useMount } from "react-use";

export interface TourStep {
  target: () => HTMLElement;
  title: string;
  description: string;
  route?: string;
}

const Tour: React.FC = () => {
  const {
    state: { run, stepIndex, steps, tourActive },
    setState,
    hasSeenTour,
    markTourSeen,
  } = useTour();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  
  // Refs to store latest values for cleanup effect
  const runRef = useRef(run);
  const markTourSeenRef = useRef(markTourSeen);
  
  // Keep refs updated with latest values
  useEffect(() => {
    runRef.current = run;
    markTourSeenRef.current = markTourSeen;
  }, [run, markTourSeen]);

  useMount(() => {
    setState({
      steps: [
        {
          target: () => document.querySelector("#job-share-section") || document.body,
          title: "Page for Candidates",
          description:
            "This is the link you'll share with job seekers. Candidates can view all your open roles and apply directly. Share it on job portals or send it directly—it's your go-to page for applicants.",
          route: "/",
        },
        {
          target: () => document.querySelector("#add-post-button") || document.body,
          title: "Create Your First Job Post",
          description:
            "Kick off your hiring process by creating a job post. Just click the button, fill in the details, and you're live!",
          route: "/job-posts",
        },
        {
          target: () => document.querySelector("#add-team-member-button") || document.body,
          title: "Invite Your Recruitment & Technical Team",
          description:
            "Add your team-mates to collaborate on hiring. They can help shortlist candidates, conduct interviews, and manage scheduling.",
          route: "/team-members",
        },
        {
          target: () => document.querySelector("#interview-settings-section") || document.body,
          title: "Opt-In for Interviews",
          description:
            "Let the system know if you or your team are available to take interviews. This helps candidates pick the right slots based on real availability.",
          route: "/interview-settings",
        },
        {
          target: () => document.querySelector("#general-availability-section") || document.body,
          title: "Set Your Available Times",
          description:
            "Set the days and times you're open for interviews. Candidates will only see these times when booking.",
          route: "/interview-settings",
        },
        {
          target: () => document.querySelector("#unavailability-section") || document.body,
          title: "Mark Your Absence",
          description:
            "Busy or on leave? Block off specific dates or times so candidates can't book interviews then.",
          route: "/interview-settings",
        },
      ],
    });
  });

  // Open/close tour based on context state
  useEffect(() => {
    setOpen(run);
  }, [run]);

  // Helper function to check if routes match (handles query params)
  const routesMatch = (stepRoute: string | undefined, currentPath: string): boolean => {
    if (!stepRoute) return false;
    // Handle routes with query params - compare only the pathname part
    if (stepRoute.includes('?')) {
      return currentPath.startsWith(stepRoute.split('?')[0]);
    }
    return stepRoute === currentPath;
  };

  // Handle navigation and step transitions
  const handleTourChange: TourProps["onChange"] = (current) => {
    const step = steps[current];
    
    // Update step index first
    setState({ stepIndex: current });
    
    // If this step requires navigation to a different route
    if (step?.route && !routesMatch(step.route, location.pathname)) {
      // Close tour before navigation to prevent overlap
      setOpen(false);
      navigate(step.route);
    }
    // If we're already on the correct route, the useEffect will handle reopening
  };

  const handleTourClose = async () => {
    setOpen(false);
    setState({ run: false, stepIndex: 0, tourActive: false });
    await markTourSeen();
  };

  // Handle next/prev navigation
  const handleTourFinish = async () => {
    console.log('[Tour] handleTourFinish — navigating to /job-posts');
    setOpen(false);
    setState({ run: false, stepIndex: 0, tourActive: false });
    await markTourSeen();
    navigate("/job-posts");
  };

  // Handle location changes to maintain tour state
  useEffect(() => {
    if (tourActive && run && steps.length > 0) {
      // Find the correct step for the current route
      const currentStep = steps[stepIndex];
      const currentRoute = location.pathname;
      
      // Check if current route matches the current step's route
      const currentRouteMatches = routesMatch(currentStep?.route, currentRoute);
      
      if (!currentRouteMatches) {
        // If route doesn't match current step, find the right step for this route
        const correctStepIndex = steps.findIndex(step => routesMatch(step.route, currentRoute));
        
        if (correctStepIndex !== -1 && correctStepIndex !== stepIndex) {
          // Update step index to match the route
          setState({ stepIndex: correctStepIndex });
          return;
        }
      }
      
      // Close tour temporarily during navigation
      setOpen(false);
      
      // Wait for DOM to be ready, then reopen tour
      let retryTimer: NodeJS.Timeout | null = null;
      const timer = setTimeout(() => {
        if (currentStep) {
          const targetElement = currentStep.target();
          if (targetElement && targetElement !== document.body) {
            setOpen(true);
          } else {
            // If target not found, try again after a bit more time
            retryTimer = setTimeout(() => {
              const retryElement = currentStep.target();
              if (retryElement && retryElement !== document.body) {
                setOpen(true);
              }
            }, 300);
          }
        }
      }, 200);

      return () => {
        clearTimeout(timer);
        if (retryTimer) {
          clearTimeout(retryTimer);
        }
      };
    }
  }, [location.pathname, tourActive, run, stepIndex, steps, setState]);

  // Cleanup effect - only runs on actual component unmount
  useEffect(() => {
    return () => {
      if (runRef.current) {
        markTourSeenRef.current();
      }
    };
  }, []); // Empty deps - only runs on unmount


  if (hasSeenTour() && !run) {
    return null;
  }

  return (
    <AntdTour
      open={open}
      onClose={handleTourClose}
      steps={steps}
      current={stepIndex}
      onChange={handleTourChange}
      onFinish={handleTourFinish}
      actionsRender={(originNode, { current, total }) => {
        if (current === steps.length - 1) {
          return [
            <Button
              key="finish"
              type="primary"
              onClick={handleTourFinish}
              style={{ marginLeft: 8 }}
            >
              Finish
            </Button>,
          ];
        }
        return originNode;
      }}
      indicatorsRender={(current, total) => `${current + 1} / ${total}`}
      mask={{
        color: "rgba(0, 0, 0, 0.5)",
        style: { zIndex: 100000 },
      }}
      gap={{ offset: 16, radius: 16 }}
      zIndex={100001}
      arrow={true}
      type="default"
    />
  );
};

export default Tour;
